import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, CheckCircle, ArrowLeft, Home, Maximize, Building, Clock, Shield, Award, ExternalLink, X, Download, FileText, ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { Logo } from '../components/Logo';
import LeadForm from '../components/LeadForm';
import LeadMagnetModal from '../components/LeadMagnetModal';
import FloorPlanPopup from '../components/FloorPlanPopup';
import Breadcrumbs from '../components/Breadcrumbs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const projectData = {
  'silver-woods': {
    name: "Silver Woods",
    headline: "Well-Designed 2 & 3 BHK Residences in Rahatani",
    offer: null,
    price: "₹72L – ₹96L",
    priceRange: "₹72L – ₹96L",
    type: "2 & 3 BHK Apartments",
    location: "Kalewadi-Rahatni, Pune",
    address: "S R. No 73/2E/3 At. Azad Chowk Kalewadi - Rahatni Road Pune - 411017",
    positioning: "Spacious layouts with rooftop amenities in Rahatani",
    rera: "P52100054401",
    whyChooseHeading: "Why Choose these 2 & 3 BHK Flats in Rahatani?",
    heroAlt: "Silver Woods Rahatani — 2 and 3 BHK apartment building exterior near DMart Thergaon and BRTS Road, Pune",
    seoLocation: "Silver Woods, 2 & 3 BHK Apartments in Kalewadi-Rahatani Pune near DMart Thergaon and BRTS Road",
    seoDescription: "Designed for young professionals working in Hinjewadi and PCMC, Silver Woods by Nova Homes offers thoughtfully crafted Premium 2 & 3 BHK Apartments in Kalewadi-Rahatani, Pune. Daily essentials are minutes away — 800 metres (a 2-minute drive) from DMart Thergaon, and 900 metres from BRTS Road for an effortless commute to Hinjewadi IT Park, Wakad, and PCMC. Easy access to the operational PCMC-Swargate Metro (Line 1 / Purple Line) via the nearby Sant Tukaram Nagar station — approximately 5 minutes by road — links you to Pimpri, Shivaji Nagar, Civil Court, and Swargate. The locality is also a short drive from Phoenix Mall of the Millennium, Wakad — placing modern lifestyle, work, and weekend retail within reach. Inside, residents get among the most spacious carpet areas in this price bracket — 763 sq.ft. for 2 BHK and 960 sq.ft. for 3 BHK — alongside rooftop lifestyle amenities including an open gym, yoga deck, and meditation space, a rare offering between ₹72 Lakhs and ₹96 Lakhs. RERA registered (P52100054401), Silver Woods is a trustworthy choice for first-home buyers and IT professionals seeking a well-connected, future-ready home in Pune's western corridor.",
    mapLink: "https://maps.app.goo.gl/NH2Hcd3GzifGqebUA",
    image: "/images/silverwoods.webp",
    projectLogo: {
      type: "image",
      src: "/images/silverwoods-logo.webp"
    },
    highlights: [
      "800m from DMart Thergaon",
      "Large carpet areas (763 & 960 sq.ft.)",
      "Rooftop lifestyle amenities",
      "Near BRTS Road & Sant Tukaram Nagar Metro",
      "Close to top schools",
      "Vastu compliant homes"
    ],
    configurations: [
      { type: "2 BHK", bedrooms: 2, carpetArea: "763 sq.ft.", carpetSqm: 70.9, priceMin: 7200000, priceMax: 8500000, rooms: "Living 10'0\"×15'5\" | Kitchen 8'6\"×8'0\" | Bedrooms 12'0\"×10'0\" & 11'0\"×10'0\"", ideal: "Couples & small families" },
      { type: "3 BHK", bedrooms: 3, carpetArea: "960 sq.ft.", carpetSqm: 89.2, priceMin: 9500000, priceMax: 9600000, rooms: "Living 14'0\"×12'0\" | Kitchen 8'6\"×8'5\" | Master Bed 10'0\"×12'10\" | Bed 10'0\"×9'6\" & 10'0\"×10'0\"", ideal: "Growing families" }
    ],
    amenities: ["Open Gym", "Yoga Space", "Meditation Space", "Walkway", "Kids Play Area", "Senior Citizen Area", "Dedicated Parking", "Rooftop Amenities", "Grand Entrance Lobby", "Fire Safety", "Power Backup", "Gated Security"],
    specifications: [
      { label: "Structure", detail: "RCC Frame Structure as per Latest Building Code" },
      { label: "Flooring", detail: "32\"×32\" or 48\"×24\" tiles in all rooms with skirting" },
      { label: "Kitchen", detail: "Granite top platform with SS sink, tiles up to lintel level" },
      { label: "Bathroom", detail: "Glazed tiles dado up to lintel, hot & cold mixer, concealed plumbing" },
      { label: "Doors", detail: "Flush doors with decorative laminate & standard fittings" },
      { label: "Windows", detail: "3-track powder coated aluminium sliding with mosquito net" },
      { label: "Lift", detail: "Johnson / Schindler / Kone / Otis or equivalent" },
      { label: "Electrical", detail: "Concealed ISI wiring, AC point in master bedroom" }
    ],
    nearby: [
      { name: "DMart Thergaon", distance: "800m · 2 min drive" },
      { name: "BRTS Road", distance: "900m · 2 min drive" },
      { name: "Hinjewadi IT Park", distance: "8 km · 15 min drive" },
      { name: "Phoenix Mall of the Millennium, Wakad", distance: "5 km · 10 min drive" },
      { name: "Sant Tukaram Nagar Metro Station (Line 1, operational)", distance: "5 min drive" },
      { name: "Orchids International School", distance: "2.6 km · 6 min drive" },
      { name: "Aditya Birla Hospital", distance: "3 km · 8 min drive" },
      { name: "Pimpri Railway Station", distance: "2 km · 5 min drive" }
    ],
    floorPlans: [
      { label: "2 BHK Layout", path: "novahomes/floorplans/silverwoods_2bhk_3d.jpg" },
      { label: "3 BHK Layout", path: "novahomes/floorplans/silverwoods_3bhk_3d.jpg" },
      { label: "Wing A — Typical Floor", path: "novahomes/floorplans/silverwoods_wing_a_floor.jpg" },
      { label: "Wing B — Typical Floor", path: "novahomes/floorplans/silverwoods_wing_b_floor.jpg" }
    ],
    gallery: [
      { label: "Building Elevation", path: "novahomes/gallery/silverwoods_page7.jpg" },
      { label: "Street View", path: "novahomes/gallery/silverwoods_page11.jpg" },
      { label: "Interior Renderings", path: "novahomes/gallery/silverwoods_page10.jpg" },
      { label: "Rooftop Amenities", path: "novahomes/gallery/silverwoods_page12.jpg" },
      { label: "Fitness & Wellness", path: "novahomes/gallery/silverwoods_page13.jpg" },
      { label: "Amenities Overview", path: "novahomes/gallery/silverwoods_page14.jpg" },
      { label: "Lifestyle", path: "novahomes/gallery/silverwoods_page4.jpg" },
      { label: "Community Living", path: "novahomes/gallery/silverwoods_page8.jpg" },
      { label: "Location Advantages", path: "novahomes/gallery/silverwoods_page2.jpg" }
    ]
  },
  'aurelia': {
    name: "Aurelia",
    headline: "Premium 3 BHK Residences in Central Rahatani",
    offer: null,
    price: "₹99.50 Lacs onwards",
    priceRange: "Starting ₹99.50L",
    type: "3 BHK Premium Apartments",
    location: "Shivraj Nagar, Rahatni, Pune",
    address: "S. No. 32/2B, Shivraj Nagar, Lane No. 1, Rahatni, Pune - 411017",
    positioning: "7-storey architectural marvel with 28 premium units in a prime central location",
    rera: "P52100077244",
    whyChooseHeading: "Why Choose this Premium 3 BHK in Rahatani?",
    heroAlt: "Aurelia Rahatani — premium 3 BHK apartment building exterior in Shivraj Nagar near BRTS Road and Phoenix Mall, Pune",
    seoLocation: "Aurelia, Premium 3 BHK Apartments in Shivraj Nagar Rahatani Pune near BRTS Road and Phoenix Mall Wakad",
    seoDescription: "Aurelia by Nova Homes is a premium low-density 3 BHK residential project in Shivraj Nagar, Rahatani, Pune — designed for senior IT professionals and discerning families working in Hinjewadi and PCMC. The 7-storey architectural development houses only 28 exclusive units with spacious 1009.50 sq.ft. carpet areas, ensuring privacy and a refined community feel. Connectivity is exceptional: just 2 minutes from BRTS Road for direct access to Hinjewadi IT Park, Wakad, and PCMC. Easy access to the operational PCMC-Swargate Metro (Line 1 / Purple Line) via the nearby Sant Tukaram Nagar station — about 5 minutes away — connects you to Pimpri, Shivaji Nagar, Civil Court, and Swargate without the highway commute. Phoenix Mall of the Millennium, Aditya Birla Hospital, Wisdom World, and SNBP International School are all within a short drive. Premium specifications include earthquake-resistant RCC structure, branded vitrified flooring, Jaquar bathroom fittings, large covered balconies, and rooftop lifestyle amenities — a cafeteria deck, barbeque zone, and landscaped terrace garden. Starting at ₹99.50 Lakhs and RERA registered (P52100077244), Aurelia is built for buyers who value quiet luxury, location, and lasting investment value in Rahatani.",
    mapLink: "https://maps.app.goo.gl/qTiBfUrRWmgnbYip7",
    image: "/images/aurelia.webp",
    hasProjectLogo: true,
    projectLogo: {
      type: "text",
      text: "Aurelia"
    },
    highlights: [
      "Just 2 mins from BRTS Highway",
      "7-storey premium building, 28 units",
      "Rooftop lifestyle amenities",
      "Low-density living — limited flats",
      "Big balconies with natural light",
      "Smart 3-tier security systems"
    ],
    configurations: [
      { type: "3 BHK", bedrooms: 3, carpetArea: "1009.50 sq.ft.", carpetSqm: 93.8, priceMin: 9950000, rooms: "Living 11'3\"×18'0\" | Kitchen 10'0\"×8'6\" | Master Bed 12'0\"×11'0\" | Bed 10'0\"×11'0\" & 12'0\"×11'0\"", ideal: "Families seeking premium living" }
    ],
    amenities: ["Fitness Center", "Open-Air Gym", "Yoga Area", "Terrace Garden", "Landscaped Gardens", "Cafeteria with Deck", "Kids Play Zone", "Barbeque Area", "Secure Parking", "Stretcher Lift", "Fire Safety", "Power Backup", "Rain Water Harvesting"],
    specifications: [
      { label: "Structure", detail: "RCC earthquake-resistant design with AAC block masonry" },
      { label: "Flooring", detail: "Vitrified branded tiles in all rooms, anti-skid on terraces" },
      { label: "Kitchen", detail: "L-shaped granite platform, SS sink, dado tiles up to lintel" },
      { label: "Bathroom", detail: "Designer tiles up to lintel, Jaquar/equivalent CP fittings, wall-hung WC" },
      { label: "Doors", detail: "Veneer finish main door with branded lock, laminated internals" },
      { label: "Windows", detail: "Sliding shutters with fly mesh for all windows" },
      { label: "Balcony", detail: "SS railing with toughened glass, all covered balconies" },
      { label: "Electrical", detail: "Schneider or equivalent switches, fire-rated copper wiring" }
    ],
    nearby: [
      { name: "BRTS Road", distance: "2 min drive" },
      { name: "DMart Thergaon", distance: "5 min drive" },
      { name: "Phoenix Mall of the Millennium, Wakad", distance: "10 min drive" },
      { name: "Hinjewadi IT Park", distance: "8 km · 15 min drive" },
      { name: "Sant Tukaram Nagar Metro Station (Line 1, operational)", distance: "5 min drive" },
      { name: "Aditya Birla Hospital", distance: "10 min drive" },
      { name: "Wisdom World School", distance: "5 min drive" },
      { name: "SNBP International School", distance: "5 min drive" },
      { name: "Indira College", distance: "10 min drive" }
    ],
    floorPlans: [
      { label: "3 BHK Unit Plan & Specifications", path: "novahomes/floorplans/aurelia_unit_plan.png" },
      { label: "Building Overview", path: "novahomes/floorplans/aurelia_typical_floor.jpg" }
    ],
    gallery: [
      { label: "Vision & Architecture", path: "novahomes/gallery/aurelia_page3.jpg" },
      { label: "Amenities Showcase", path: "novahomes/gallery/aurelia_page5.jpg" },
      { label: "Lifestyle & Features", path: "novahomes/gallery/aurelia_page6.jpg" },
      { label: "Developer Profile", path: "novahomes/gallery/aurelia_page2.jpg" },
      { label: "Location & Overview", path: "novahomes/gallery/aurelia_page1.jpg" }
    ]
  },
  'sneha-avenue': {
    name: "Sneha Avenue",
    headline: "Spacious 2, 3 BHK Homes & Duplexes in Bhugaon",
    offer: null,
    price: "Starting ₹70L",
    priceRange: "Starting ₹70L",
    type: "2, 3 BHK & Duplex",
    location: "Bhugaon, Pune",
    address: "S.No 263/1 Opp. Daulat Lawns, Bhugaon, Pune - 411023",
    positioning: "Bespoke homes designed for personal space and contemporary living",
    rera: "P52100049963",
    whyChooseHeading: "Why Choose these 2 & 3 BHK Flats and Duplexes in Bhugaon, Pune?",
    heroAlt: "Sneha Avenue Bhugaon — 2, 3 BHK and Duplex apartment building exterior near Chandani Chowk and Mumbai-Bangalore Bypass, Pune",
    seoLocation: "Sneha Avenue, 2 & 3 BHK Apartments and Duplexes in Bhugaon Pune near Chandani Chowk and Mumbai-Bangalore Bypass",
    seoDescription: "Sneha Avenue by Nova Homes is a serene residential project in Bhugaon, Pune offering Premium 2 BHK, 3 BHK Apartments and exclusive Duplex residences starting at just ₹70 Lakhs — an ideal choice for first-time home buyers and young families looking to balance city convenience with a calmer, nature-led lifestyle. The project is 2.8 km from Chandani Chowk, one of Pune's most strategic junctions, giving residents quick access to Hinjewadi IT Park, the Mumbai-Bangalore Bypass, NDA, and central Pune (Bawdhan, Kothrud, Karve Road) — all within a 15 to 25-minute drive. Quick connectivity to the operational Pune Metro is available via Vanaz station of the Vanaz-Ramwadi line (Line 2 / Aqua Line), just a short drive away — linking you to Kothrud, Deccan, Pune Station, and Ramwadi. Surrounded by hills and greenery, Sneha Avenue offers a refreshing low-traffic alternative for IT professionals seeking peace after work, while still being close enough to top schools, Aditya Birla Hospital, and weekend retail. Construction features earthquake-resistant RCC, solar water heating, rainwater harvesting, and generously sized rooms in every configuration. RERA registered (P52100049963), Sneha Avenue blends affordability, future appreciation, and quality of life in one address.",
    mapLink: "https://maps.app.goo.gl/m12qSTLYpuFKRevv7",
    image: "/images/sneha-avenue.webp",
    highlights: [
      "Budget-friendly starting price",
      "2.8 km from Chandani Chowk",
      "Earthquake-resistant RCC structure",
      "Surrounded by nature & greenery",
      "Bespoke duplex options",
      "Quality construction standards"
    ],
    configurations: [
      { type: "2 BHK", bedrooms: 2, carpetArea: "Spacious layout", priceMin: 7000000, rooms: "Living 11'9\"×19'8\" | Kitchen 9'3\"×7'10\" | Bedrooms 10'3\"×12'6\" & 11'2\"×12'6\"", ideal: "First-time buyers" },
      { type: "3 BHK", bedrooms: 3, carpetArea: "Large family layout", priceMin: 8500000, rooms: "Living 12'6\"×23'4\" | Kitchen 9'6\"×7'9\" | Bedrooms 10'8\"×13'5\", 11'10\"×10'10\" & 12'10\"×13'5\"", ideal: "Growing families" },
      { type: "Duplex", bedrooms: 4, carpetArea: "Double-floor premium", rooms: "Expansive duplex layout with customization options", ideal: "Those seeking luxury space" }
    ],
    amenities: ["Grand Entrance with Security", "Children's Play Area", "Elegant Compound Wall", "Senior Citizen Zone", "Boundary Plantation", "Rain Water Harvesting", "Solar Water Connection", "Generator Backup", "Safety Grills", "Fire Safety", "Power Backup"],
    specifications: [
      { label: "Structure", detail: "Earthquake-resistant RCC, 5\" AAC block walls, gypsum finish" },
      { label: "Flooring", detail: "2'×2' tiles in entire flat, 16\"×16\" in passage areas" },
      { label: "Kitchen", detail: "Steel grey granite platform, SS sink, dado tiles up to 7ft" },
      { label: "Bathroom", detail: "Wall tiles up to 7ft, concealed plumbing, quality CP fittings" },
      { label: "Doors", detail: "Laminated main door, internal flush doors, PVC bathroom doors" },
      { label: "Windows", detail: "3-track powder coated aluminium sliding with granite sills" },
      { label: "Painting", detail: "Oil bond distemper internal, cement paint external" },
      { label: "Electrical", detail: "Concealed wiring, adequate points, standard ISI switches" }
    ],
    nearby: [
      { name: "Chandani Chowk Junction", distance: "2.8 km · 8 min drive" },
      { name: "Vanaz Metro Station (Line 2, operational)", distance: "10 min drive" },
      { name: "Hinjewadi IT Park (via Bypass)", distance: "15 km · 25 min drive" },
      { name: "Bawdhan", distance: "3.5 km · 10 min drive" },
      { name: "Kothrud Depot", distance: "5.8 km · 15 min drive" },
      { name: "DMart Bavdhan", distance: "10 min drive" },
      { name: "NDA Khadakwasla", distance: "10 min drive" },
      { name: "Mumbai-Bangalore Bypass", distance: "5 min drive" },
      { name: "Aditya Birla Hospital", distance: "20 min drive" }
    ],
    floorPlans: [
      { label: "3 BHK & Duplex Layouts", path: "novahomes/floorplans/snehaavenue_1st_floor.jpg" },
      { label: "2 BHK Layout", path: "novahomes/floorplans/snehaavenue_2nd_floor.jpg" },
      { label: "Typical Floor Plan — Lower", path: "novahomes/floorplans/snehaavenue_3rd_floor.jpg" },
      { label: "Typical Floor Plan — Upper", path: "novahomes/floorplans/snehaavenue_4th_floor.jpg" }
    ],
    gallery: [
      { label: "Building Elevation", path: "novahomes/gallery/snehaavenue_page3.jpg" },
      { label: "Luxury Living", path: "novahomes/gallery/snehaavenue_page2.jpg" },
      { label: "Specifications & Amenities", path: "novahomes/gallery/snehaavenue_page5.jpg" },
      { label: "Location Map", path: "novahomes/gallery/snehaavenue_page8.jpg" }
    ]
  }
};

const ProjectPage = () => {
  const { slug } = useParams();
  const project = projectData[slug];
  const whatsappNumber = "917498963176";

  const [scrolled, setScrolled] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Akshaya Tritiya offer popup — expires after 20th April 2026
  const expiryDate = new Date('2026-04-20T00:00:00');
  const showFestiveOffer = new Date() < expiryDate;
  const [popupOpen, setPopupOpen] = useState(false);

  // Exclusive Offers dropdown
  const [offersOpen, setOffersOpen] = useState(false);
  const [floorPlansLeadOpen, setFloorPlansLeadOpen] = useState(false);
  const [brochureLeadOpen, setBrochureLeadOpen] = useState(false);
  // New unified "Get Floor Plans & Price Sheet" popup (Mobile + Name only)
  const [floorPlanPopupOpen, setFloorPlanPopupOpen] = useState(false);
  const [floorPlanPopupSource, setFloorPlanPopupSource] = useState('project-cta');
  const location = useLocation();
  const navigate = useNavigate();

  const offersProjects = Object.entries(projectData).map(([s, p]) => ({
    name: p.name, slug: s, type: p.type, price: p.priceRange || p.price, image: p.image
  }));

  const handleOfferClick = (targetSlug) => {
    setOffersOpen(false);
    if (targetSlug === slug) {
      setPopupOpen(true);
    } else {
      navigate(`/project/${targetSlug}`, { state: { openOfferPopup: true } });
    }
  };

  // Trigger popup from dropdown redirect
  useEffect(() => {
    if (location.state?.openOfferPopup) {
      setPopupOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') setOffersOpen(false); };
    if (offersOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [offersOpen]);

  // Floor Plans + Price Sheet popup auto-trigger disabled — opens only via CTA buttons now

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const navigateLightbox = useCallback((dir) => {
    if (!project?.gallery) return;
    setLightboxIndex(prev => (prev + dir + project.gallery.length) % project.gallery.length);
  }, [project]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateLightbox(1);
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, closeLightbox, navigateLightbox]);

  const handleDownload = async (imagePath, label) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/floorplans/${imagePath}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replace(/\s+/g, '_')}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug]);

  // Dynamic SEO title
  useEffect(() => {
    if (!project) {
      return () => { document.title = 'Nova Homes Pune | 2 & 3 BHK Flats in Rahatani | Book Site Visit'; };
    }

    document.title = `${project.name} | ${project.type} in Rahatani, Pune | Nova Homes`;

    // === Per-unit Product / Apartment Schema (Schema.org) ===
    // One Apartment-type Product per BHK configuration on this project page.
    const projectUrl = `https://novahomes.in/project/${project.slug}`;
    const projectImage = project.image?.startsWith('http') ? project.image : `https://novahomes.in${project.image || '/images/silverwoods.webp'}`;

    const unitProducts = (project.configurations || []).map((cfg) => {
      const carpetSqft = parseFloat((cfg.carpetArea || '').match(/[\d.]+/)?.[0]);
      const offers = cfg.priceMax
        ? {
            "@type": "AggregateOffer",
            "lowPrice": cfg.priceMin,
            "highPrice": cfg.priceMax,
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "url": projectUrl,
            "seller": { "@type": "Organization", "name": "Nova Homes" }
          }
        : cfg.priceMin
        ? {
            "@type": "Offer",
            "price": cfg.priceMin,
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "url": projectUrl,
            "seller": { "@type": "Organization", "name": "Nova Homes" }
          }
        : undefined;

      const additionalProperty = [
        { "@type": "PropertyValue", "name": "RERA Registration", "value": project.rera || "" },
        { "@type": "PropertyValue", "name": "Configuration", "value": cfg.type },
        { "@type": "PropertyValue", "name": "Carpet Area", "value": cfg.carpetArea },
      ].filter(p => p.value);

      const product = {
        "@context": "https://schema.org",
        "@type": ["Product", "Apartment"],
        "name": `${project.name} — ${cfg.type} Apartment in Rahatani Pune`,
        "description": `${cfg.type} apartment at ${project.name}, ${project.location || 'Rahatani, Pune'}. Carpet area ${cfg.carpetArea}. Ideal for ${cfg.ideal || 'families'}.`,
        "url": projectUrl,
        "image": projectImage,
        "brand": { "@type": "Brand", "name": "Nova Homes" },
        "category": "Real Estate Apartment",
        "numberOfRooms": cfg.bedrooms,
        "numberOfBedrooms": cfg.bedrooms,
        ...(carpetSqft && !Number.isNaN(carpetSqft) ? { "floorSize": { "@type": "QuantitativeValue", "value": carpetSqft, "unitCode": "FTK" } } : {}),
        "additionalProperty": additionalProperty,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "12",
          "bestRating": "5",
          "worstRating": "1"
        },
      };
      if (offers) product.offers = offers;
      return product;
    });

    // Wrap all units in an ItemList for clean grouping
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `${project.name} — Available Configurations`,
      "itemListElement": (project.configurations || []).map((cfg, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": `${project.name} — ${cfg.type}`,
        "url": projectUrl,
      })),
    };

    // BreadcrumbList schema — helps crawlers understand hierarchy
    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://novahomes.in/" },
        { "@type": "ListItem", "position": 2, "name": "Projects", "item": "https://novahomes.in/" },
        { "@type": "ListItem", "position": 3, "name": project.name, "item": projectUrl }
      ]
    };

    const scripts = [];
    [breadcrumbList, itemList, ...unitProducts].forEach((schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(schema);
      s.setAttribute('data-project-schema', project.slug);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      scripts.forEach(s => { try { document.head.removeChild(s); } catch (_) {} });
      document.title = 'Nova Homes Pune | 2 & 3 BHK Flats in Rahatani | Book Site Visit';
    };
  }, [project, slug]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-semibold mb-4">Project Not Found</h2>
          <Link to="/" className="text-white/50 hover:text-white transition-colors">Go Back Home</Link>
        </div>
      </div>
    );
  }

  const whatsappMessage = encodeURIComponent(`Hi, I'm interested in ${project.name}.`);

  return (
    <div data-testid={`project-page-${slug}`} className="min-h-screen bg-white">
      
      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-[80vh] bg-[#111111]">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={project.image}
            alt={project.heroAlt || project.name}
            className="w-full h-full object-cover animate-hero-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/85 to-[#111111]/60" />
        </div>

        {/* Top Bar */}
        <div className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 lg:px-16 flex items-center justify-between transition-all duration-300 ${scrolled ? 'py-1.5 bg-[#111111]/95 backdrop-blur-sm shadow-lg' : 'py-2'}`}>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Logo variant="light" scrolled={scrolled} />
              {project.projectLogo && (
                <>
                  <div className="w-[1px] bg-white/20 self-stretch my-2" style={{ minHeight: scrolled ? '40px' : '60px' }} />
                  {project.projectLogo.type === "image" ? (
                    <img 
                      src={project.projectLogo.src}
                      alt={`${project.name} project logo — ${project.seoLocation || project.name}`}
                      className="object-contain transition-all duration-300"
                      style={{ height: scrolled ? '50px' : '80px' }}
                    />
                  ) : (
                    <span 
                      className="font-heading text-[#B5A17C] tracking-[0.35em] uppercase font-semibold whitespace-nowrap"
                      style={{ fontSize: scrolled ? '18px' : '32px', transition: 'font-size 0.3s ease', lineHeight: 1.1 }}
                    >
                      {project.projectLogo.text}
                    </span>
                  )}
                </>
              )}
            </div>
            <Link 
              to="/" 
              data-testid="back-to-home"
              className="hidden md:flex items-center gap-1 text-white/40 hover:text-white/70 text-sm font-light transition-colors"
            >
              <ArrowLeft size={15} />
              Back to Home
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              data-testid="header-insights-link"
              className="hidden md:inline-block text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors mr-2"
            >
              Insights
            </Link>
            {showFestiveOffer && (
              <button
                onClick={() => setOffersOpen(!offersOpen)}
                data-testid="exclusive-offers-btn"
                className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 text-[10px] md:text-xs uppercase tracking-[0.15em] md:tracking-[0.2em] font-medium transition-all duration-300 ${offersOpen ? 'text-[#0f0a04]' : 'text-[#C5A46D] border border-[#C5A46D]/30 hover:border-[#C5A46D]/60'}`}
                style={offersOpen ? { background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)' } : {}}
              >
                {!offersOpen && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse hidden md:block" />}
                <span className="hidden md:inline">Exclusive Offers</span>
                <span className="md:hidden">Offers</span>
              </button>
            )}
            <a 
              href={`tel:+917498963176`}
              data-testid="call-now-btn"
              className="flex items-center gap-2 border border-white/15 text-white hover:bg-white/5 px-5 py-2.5 font-medium text-sm transition-colors tracking-wide"
            >
              <Phone size={15} />
              <span className="hidden sm:inline">Call Now</span>
            </a>
          </div>
        </div>

        {/* Exclusive Offers - Overlay */}
        {showFestiveOffer && offersOpen && (
          <div 
            data-testid="offers-overlay"
            className="fixed inset-0 z-[48] bg-black/40 backdrop-blur-sm"
            onClick={() => setOffersOpen(false)}
          />
        )}

        {/* Exclusive Offers - Dropdown Panel */}
        <div 
          data-testid="offers-dropdown-panel"
          className={`fixed left-0 right-0 z-[49] px-4 transition-all duration-300 ease-out ${offersOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
          style={{ top: scrolled ? '48px' : '56px' }}
        >
          <div className="max-w-4xl mx-auto">
            <div 
              className="backdrop-blur-xl border border-[#C5A46D]/15"
              style={{ background: 'linear-gradient(170deg, #0f0a04 0%, #1a1208 40%, #0f0a04 100%)', boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 1px rgba(212,175,55,0.3)' }}
            >
              <div className="h-[1px] w-full" style={{ background: 'linear-gradient(90deg, transparent 5%, #D4AF37 30%, #F5E6C4 50%, #D4AF37 70%, transparent 95%)' }} />
              <div className="p-5 md:p-8">
                <div className="text-center mb-5">
                  <p className="text-[10px] uppercase tracking-[0.5em] mb-2" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Akshaya Tritiya Special</p>
                  <p className="text-white/30 text-xs font-light tracking-wide">No Stamp Duty · No Registration · No GST</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  {offersProjects.map((p) => (
                    <button key={p.slug} onClick={() => handleOfferClick(p.slug)} data-testid={`offer-project-${p.slug}`} className="group text-left p-3 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.06] hover:border-[#C5A46D]/30 transition-all duration-300">
                      <div className="aspect-[16/10] mb-3 overflow-hidden">
                        <img src={p.image} alt={`${p.name} — ${p.type}, ${p.price}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <h4 className="font-heading text-white text-base md:text-lg font-semibold mb-0.5">{p.name}</h4>
                      <p className="text-white/35 text-xs font-light mb-1.5">{p.type}</p>
                      <p className="text-sm font-medium" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6C4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{p.price}</p>
                    </button>
                  ))}
                </div>
                <p className="text-center text-white/10 text-[10px] mt-4 tracking-[0.15em]">*CONDITIONS APPLY · VALID TILL 19TH APRIL 2026</p>
              </div>
              <div className="h-[1px] w-full" style={{ background: 'linear-gradient(90deg, transparent 5%, #D4AF37 30%, #F5E6C4 50%, #D4AF37 70%, transparent 95%)' }} />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 container-custom min-h-[calc(80vh-80px)] flex flex-col pt-56 pb-16">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumbs items={[
              { label: 'Home', href: '/' },
              { label: 'Projects', href: '/' },
              { label: project.name }
            ]} />
          </div>
          <div className="flex-1 flex items-end">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16 w-full items-end">
            
            {/* Left Content */}
            <div className="lg:col-span-3 text-white">
              {/* Trust badges — Direct from Builder + Verified On */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span data-testid="direct-from-builder-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-medium" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6C4)', color: '#0a0a0a' }}>
                  ✦ Direct from Builder · 0% Brokerage
                </span>
                <span data-testid="verified-on-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-light text-white/50 border border-white/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A46D] animate-pulse" />
                  Pricing verified · 28 Apr 2026
                </span>
              </div>

              {/* Type Badge */}
              <div className="inline-block border border-white/15 text-white/60 px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] mb-8">
                {project.type}
              </div>

              {/* Headline */}
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] mb-5 text-shadow-hero text-left">
                {project.headline}
              </h1>

              {/* Location + Map Link */}
              <div className="flex items-center gap-2 text-white/80 mb-6 font-light">
                <MapPin size={16} className="flex-shrink-0" />
                <span>{project.location}</span>
                <a 
                  href={project.mapLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="map-link"
                  className="inline-flex items-center gap-1 text-[#C8B48F] hover:text-[#D4C4A0] transition-colors ml-2 animate-map-glow"
                >
                  <ExternalLink size={13} />
                  <span className="text-xs uppercase tracking-wider">View on Map</span>
                </a>
              </div>

              {/* Price + RERA */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-8 mb-8">
                <div className="border-l border-white/15 pl-5 inline-block">
                  <p className="text-white/30 text-xs uppercase tracking-[0.2em] mb-1">Price Range</p>
                  <p className="text-2xl md:text-3xl font-semibold text-white">{project.price}</p>
                </div>
                <div className="border-l border-white/15 pl-5 inline-block">
                  <p className="text-white/30 text-xs uppercase tracking-[0.2em] mb-1">RERA</p>
                  <p className="text-sm text-white/60 font-light">{project.rera}</p>
                </div>
              </div>

              {/* Positioning */}
              <p className="text-white/75 text-base mb-10 max-w-xl font-light leading-relaxed text-shadow-hero text-left">
                {project.positioning}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setFloorPlanPopupSource(`project-${slug}-hero`); setFloorPlanPopupOpen(true); }}
                  data-testid="hero-cta-floor-plans"
                  className="px-8 py-4 font-semibold text-sm tracking-[0.12em] uppercase text-[#0f0a04] flex items-center justify-center gap-2 hover:brightness-110 transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)',
                    boxShadow: '0 4px 18px rgba(212,175,55,0.25)'
                  }}
                >
                  <FileText size={16} />
                  Get Floor Plans &amp; Price Sheet
                </button>
                <a
                  href="#book-visit"
                  data-testid="hero-cta-book"
                  className="bg-white text-[#111111] hover:bg-white/90 px-8 py-4 font-medium text-center tracking-wide transition-colors"
                >
                  Schedule a Site Visit
                </a>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="border border-white/15 text-white hover:bg-white/5 px-8 py-4 font-medium text-center transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle size={17} />
                  Enquire on WhatsApp
                </a>
              </div>
            </div>

            {/* Right - Lead Form */}
            <div className="lg:col-span-2 hidden lg:block self-center">
              <div className="bg-white p-8 shadow-2xl">
                <h3 className="font-heading text-lg font-semibold text-[#111111] mb-2 text-center">
                  Schedule a Site Visit
                </h3>
                <p className="text-[#999999] text-sm mb-6 text-center font-light">
                  Response within 30 minutes
                </p>
                <LeadForm variant="light" source={`project-${slug}`} projectName={project.name} />
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* ========== HIGHLIGHTS ========== */}
      <section data-testid="highlights-section" className="py-20 md:py-32 bg-[#F7F7F5]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                {project.whyChooseHeading || `Why ${project.name}?`}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.highlights.map((highlight, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-3 p-5 bg-white border-l border-[#111111]/10"
                >
                  <CheckCircle size={16} className="text-[#111111]/30 flex-shrink-0" />
                  <span className="text-[#111111] font-medium text-sm">{highlight}</span>
                </div>
              ))}
            </div>

            {/* SEO Description — improves word count and text-to-HTML ratio */}
            {project.seoDescription && (
              <div className="mt-12 pt-10 border-t border-[#111]/[0.06]">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-[#111111] mb-4">
                  About {project.name} — {project.type} in {project.location.split(',')[0]}
                </h2>
                <p className="text-[#555] text-sm md:text-base font-light leading-[1.9]">
                  {project.seoDescription}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ========== CONFIGURATIONS ========== */}
      <section data-testid="configurations-section" className="py-20 md:py-32 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Floor Plans</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                Available Configurations
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {project.configurations.map((config, i) => (
                <div 
                  key={i}
                  data-testid={`config-card-${i}`}
                  className="p-7 bg-[#F7F7F5] hover:shadow-lg transition-all duration-500"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-[#111111]/[0.04] flex items-center justify-center">
                      <Home size={20} className="text-[#111111]/40" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-[#111111]">{config.type}</h3>
                    </div>
                  </div>
                  
                  {/* Carpet Area */}
                  <div className="flex items-center gap-2 mb-3">
                    <Maximize size={14} className="text-[#B5A17C]" />
                    <span className="text-[#111111] text-sm font-medium">Carpet: {config.carpetArea}</span>
                  </div>

                  {/* Room Dimensions */}
                  <p className="text-[#666666] text-xs mb-4 font-light leading-relaxed border-l-2 border-[#111111]/5 pl-3">
                    {config.rooms}
                  </p>

                  <p className="text-[#999999] text-sm mb-5 font-light">Ideal for: {config.ideal}</p>
                  
                  <a
                    href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hi, I'd like to know more about ${config.type} at ${project.name}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`config-enquire-${i}`}
                    className="w-full bg-[#111111] hover:bg-[#222222] text-white py-3 font-medium text-sm text-center flex items-center justify-center gap-2 transition-colors tracking-wide"
                  >
                    <MessageCircle size={14} />
                    Enquire
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FLOOR PLANS ========== */}
      {project.floorPlans && project.floorPlans.length > 0 && (
        <section data-testid="floor-plans-section" className="py-20 md:py-32 bg-[#111111]">
          <div className="container-custom">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Brochure</p>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3">
                  Floor Plans
                </h2>
              </div>

              {/* Tab buttons */}
              <div className="flex flex-wrap justify-center gap-2 mb-10">
                {project.floorPlans.map((plan, i) => (
                  <button
                    key={i}
                    data-testid={`floor-plan-tab-${i}`}
                    onClick={() => setSelectedPlan(i)}
                    className={`px-5 py-2.5 text-sm font-light tracking-wide transition-colors ${
                      selectedPlan === i
                        ? 'bg-white text-[#111111]'
                        : 'border border-white/15 text-white/60 hover:text-white hover:border-white/30'
                    }`}
                  >
                    {plan.label}
                  </button>
                ))}
              </div>

              {/* Floor plan image */}
              <div className="bg-white p-2 md:p-4">
                <img
                  src={`${BACKEND_URL}/api/floorplans/${project.floorPlans[selectedPlan].path}`}
                  alt={`${project.floorPlans[selectedPlan].label} — ${project.seoLocation || project.name}`}
                  data-testid="floor-plan-image"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-white/20 text-xs text-center mt-4 font-light">
                {project.floorPlans[selectedPlan].label} — {project.name}
              </p>

              {/* Download Floor Plans PDF lead-magnet */}
              <div className="mt-12 max-w-2xl mx-auto bg-white/[0.04] border border-white/[0.08] p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
                <div className="w-12 h-12 bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center flex-shrink-0 rounded-sm">
                  <Download size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium mb-1.5">Free PDF · All Layouts</p>
                  <h3 className="font-heading text-lg md:text-xl text-white font-semibold mb-1">
                    Download All Floor Plans as PDF
                  </h3>
                  <p className="text-white/50 text-xs md:text-sm font-light">
                    Print-quality unit layouts, typical floors, carpet areas, and RERA — in one file.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFloorPlansLeadOpen(true)}
                  data-testid="download-floor-plans-pdf"
                  className="group inline-flex items-center justify-center gap-2 bg-[#C5A46D] text-[#0a0a0a] py-3 px-5 font-semibold text-sm tracking-wide hover:bg-[#D4AF37] transition-colors flex-shrink-0"
                >
                  <Download size={15} />
                  Get PDF
                </button>
              </div>

              {/* Download Full Project Brochure lead-magnet — only if the project has an official rebranded PDF */}
              {['silver-woods', 'aurelia'].includes(slug) && (
                <div className="mt-4 max-w-2xl mx-auto bg-white/[0.04] border border-white/[0.08] p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
                  <div className="w-12 h-12 bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center flex-shrink-0 rounded-sm">
                    <FileText size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium mb-1.5">Official Brochure · PDF</p>
                    <h3 className="font-heading text-lg md:text-xl text-white font-semibold mb-1">
                      Get Floor Plans &amp; Price Sheet — {project.name}
                    </h3>
                    <p className="text-white/50 text-xs md:text-sm font-light">
                      Detailed floor plans, carpet areas, latest pricing &amp; current availability — sent by our team.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setFloorPlanPopupSource(`project-${slug}-floor-plans-section`); setFloorPlanPopupOpen(true); }}
                    data-testid="download-project-brochure-pdf"
                    className="group inline-flex items-center justify-center gap-2 bg-[#C5A46D] text-[#0a0a0a] py-3 px-5 font-semibold text-sm tracking-wide hover:bg-[#D4AF37] transition-colors flex-shrink-0"
                  >
                    <FileText size={15} />
                    Get Floor Plans &amp; Price Sheet
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ========== AMENITIES ========== */}
      <section data-testid="amenities-section" className="py-20 md:py-32 bg-[#F7F7F5]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Lifestyle</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                Amenities
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {project.amenities.map((amenity, i) => (
                <span
                  key={i}
                  data-testid={`amenity-${i}`}
                  className="px-5 py-2.5 bg-white text-[#111111] text-sm font-light tracking-wide"
                >
                  {amenity}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== GALLERY ========== */}
      {project.gallery && project.gallery.length > 0 && (
        <section data-testid="gallery-section" className="py-20 md:py-32 bg-white">
          <div className="container-custom">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Brochure</p>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                  Gallery
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {project.gallery.map((item, i) => (
                  <div 
                    key={i}
                    data-testid={`gallery-item-${i}`}
                    className="group relative cursor-pointer overflow-hidden bg-[#F7F7F5] aspect-[16/10]"
                    onClick={() => openLightbox(i)}
                  >
                    <img 
                      src={`${BACKEND_URL}/api/floorplans/${item.path}`}
                      alt={`${item.label} — ${project.seoLocation || project.name}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-[#111111]/0 group-hover:bg-[#111111]/50 transition-colors duration-300 flex items-end">
                      <div className="w-full p-3 md:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <p className="text-white text-sm font-light tracking-wide">{item.label}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== SPECIFICATIONS ========== */}
      <section data-testid="specifications-section" className="py-20 md:py-32 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Quality</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                Specifications
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              {project.specifications.map((spec, i) => (
                <div 
                  key={i}
                  data-testid={`spec-${i}`}
                  className="py-4 border-b border-[#111111]/[0.06]"
                >
                  <p className="text-[#111111] font-medium text-sm mb-1">{spec.label}</p>
                  <p className="text-[#666666] text-sm font-light leading-relaxed">{spec.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== LOCATION & NEARBY ========== */}
      <section data-testid="location-section" className="py-20 md:py-32 bg-[#F7F7F5]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Connectivity & Location</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                Connectivity &amp; Location
              </h2>
              <p className="text-[#999999] font-light text-sm">{project.address}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
              {project.nearby.map((place, i) => (
                <div 
                  key={i}
                  data-testid={`nearby-${i}`}
                  className="p-5 bg-white"
                >
                  <p className="text-[#111111] font-medium text-sm mb-1">{place.name}</p>
                  <p className="text-[#999999] text-xs font-light">{place.distance}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <a
                href={project.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-on-map-btn"
                className="inline-flex items-center gap-2 bg-[#111111] hover:bg-[#222222] text-white px-8 py-4 font-medium text-sm tracking-wide transition-colors"
              >
                <MapPin size={16} />
                View on Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PROMINENT FLOOR PLANS + PRICE SHEET CTA BANNER ========== */}
      <section data-testid="floor-plans-cta-banner" className="py-16 md:py-24 bg-[#0c0906] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ background: 'radial-gradient(circle at 30% 50%, #D4AF37 0%, transparent 55%)' }}
        />
        <div className="container-custom relative">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#C5A46D] text-[11px] uppercase tracking-[0.35em] font-medium mb-5">Direct from {project.name}</p>
            <h2 className="font-heading text-2xl md:text-4xl font-semibold text-white mb-4 leading-tight">
              Get Floor Plans &amp; Price Sheet
            </h2>
            <p className="text-white/55 text-sm md:text-base font-light leading-relaxed max-w-2xl mx-auto mb-8">
              View detailed floor plans, carpet area, latest pricing and current availability for {project.name} — sent by our team within 30 minutes.
            </p>
            <button
              type="button"
              onClick={() => { setFloorPlanPopupSource(`project-${slug}-cta-banner`); setFloorPlanPopupOpen(true); }}
              data-testid="project-floor-plans-cta-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 font-semibold text-sm uppercase tracking-[0.18em] text-[#0f0a04] hover:brightness-110 transition-all"
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)',
                boxShadow: '0 4px 22px rgba(212,175,55,0.28)'
              }}
            >
              <FileText size={16} />
              Get Floor Plans &amp; Price Sheet
            </button>
            <p className="text-white/30 text-[11px] mt-5 tracking-wide">No spam · Direct line to Nova Homes team</p>
          </div>
        </div>
      </section>

      {/* ========== TRUST SECTION ========== */}
      <section data-testid="trust-section" className="py-20 md:py-32 bg-[#111111]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center gap-12 md:gap-16 mb-10">
              <div className="text-center">
                <Shield size={24} className="text-white/20 mx-auto mb-3" />
                <p className="text-white font-medium text-sm">0% Brokerage</p>
                <p className="text-white/30 text-xs font-light mt-1">Direct pricing</p>
              </div>
              <div className="text-center">
                <Award size={24} className="text-white/20 mx-auto mb-3" />
                <p className="text-white font-medium text-sm">RERA Registered</p>
                <p className="text-white/30 text-xs font-light mt-1">{project.rera}</p>
              </div>
              <div className="text-center">
                <Clock size={24} className="text-white/20 mx-auto mb-3" />
                <p className="text-white font-medium text-sm">Quick Response</p>
                <p className="text-white/30 text-xs font-light mt-1">30 min callback</p>
              </div>
            </div>
            
            <div className="w-8 h-[1px] bg-white/10 mx-auto mb-6"></div>
            <p className="text-white/30 font-light text-sm">
              <span className="text-white/50">Nishant Bhamre</span> – Nova Homes
            </p>
          </div>
        </div>
      </section>

      {/* ========== EXPLORE OTHER PROJECTS — INTERNAL LINKING ========== */}
      <section data-testid="other-projects-section" className="py-20 md:py-28 bg-[#F7F7F5] border-t border-[#111]/[0.04]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">More from Nova Homes</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                Explore Our Other Premium Projects in Pune
              </h2>
              <p className="text-[#999999] font-light text-sm max-w-xl mx-auto">
                Hand-picked 2 &amp; 3 BHK residences across Rahatani and Bhugaon
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {Object.entries(projectData)
                .filter(([s]) => s !== slug)
                .map(([s, p]) => {
                  // Comparison microcopy: price + carpet area delta vs current project
                  const curPriceMin = Math.min(...(project.configurations || []).map(c => c.priceMin || Infinity));
                  const otherPriceMin = Math.min(...(p.configurations || []).map(c => c.priceMin || Infinity));
                  const priceDiff = otherPriceMin - curPriceMin;
                  const priceLabel = priceDiff === 0 || !isFinite(curPriceMin) || !isFinite(otherPriceMin)
                    ? null
                    : priceDiff > 0
                      ? `₹${Math.round(priceDiff / 100000)}L more`
                      : `₹${Math.round(Math.abs(priceDiff) / 100000)}L less`;

                  const curCarpet = Math.max(...(project.configurations || []).map(c => parseFloat((c.carpetArea || '').match(/[\d.]+/)?.[0]) || 0));
                  const otherCarpet = Math.max(...(p.configurations || []).map(c => parseFloat((c.carpetArea || '').match(/[\d.]+/)?.[0]) || 0));
                  const carpetDiff = Math.round(otherCarpet - curCarpet);
                  const carpetLabel = !curCarpet || !otherCarpet || carpetDiff === 0
                    ? null
                    : carpetDiff > 0
                      ? `${carpetDiff} sqft larger`
                      : `${Math.abs(carpetDiff)} sqft smaller`;

                  return (
                    <Link
                      key={s}
                      to={`/project/${s}`}
                      data-testid={`other-project-card-${s}`}
                      className="group bg-white hover:shadow-2xl transition-all duration-500 overflow-hidden"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={p.image}
                          alt={`${p.name} — ${p.type} in ${p.location.split(',')[0]} Pune, ${p.priceRange || p.price}`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                        />
                      </div>
                      <div className="p-7">
                        {(priceLabel || carpetLabel) && (
                          <div className="flex flex-wrap gap-2 mb-3" data-testid={`comparison-chips-${s}`}>
                            {priceLabel && (
                              <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-medium bg-[#B5A17C]/10 text-[#8B7B5E] border border-[#B5A17C]/20">
                                {priceLabel}
                              </span>
                            )}
                            {carpetLabel && (
                              <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-medium bg-[#B5A17C]/10 text-[#8B7B5E] border border-[#B5A17C]/20">
                                {carpetLabel}
                              </span>
                            )}
                          </div>
                        )}
                        <h3 className="font-heading text-xl font-semibold text-[#111111] mb-2 group-hover:text-[#B5A17C] transition-colors">
                          {p.name} — {p.type} in {p.location.split(',')[0]}
                        </h3>
                        <p className="text-[#666666] text-sm mb-5 leading-relaxed font-light">
                          {p.positioning}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-[#111111] font-medium text-sm">{p.priceRange || p.price}</span>
                          <span className="inline-flex items-center gap-1.5 text-[#111111]/50 font-medium text-sm group-hover:text-[#111111] transition-colors">
                            View Project <ArrowLeft size={13} className="rotate-180" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </section>

      {/* ========== LOCAL GUIDES & INSIGHTS — INTERNAL LINKING ========== */}
      <section data-testid="local-guides-section" className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-4">Local Guides</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111]">
                Learn More About {project.location.split(',')[0]} &amp; Pune Real Estate
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { to: '/rahatani-guide-2026', label: 'Rahatani Guide 2026 — Complete Area Overview', tag: 'Area Guide' },
                { to: '/flats-near-dmart-thergaon', label: 'Flats Near DMart Thergaon — Walk to Daily Essentials', tag: 'Location' },
                { to: '/3-bhk-collection-rahatani', label: '3 BHK Premium Collection in Rahatani, Pune', tag: 'Collection' },
                { to: '/blog', label: 'Buyer Guides, Price Trends &amp; Market Insights', tag: 'Insights' }
              ].map((g, i) => (
                <Link
                  key={i}
                  to={g.to}
                  data-testid={`local-guide-${i}`}
                  className="group block p-5 bg-[#F7F7F5] hover:bg-[#111111] transition-colors duration-300"
                >
                  <p className="text-[#B5A17C] text-[10px] uppercase tracking-[0.25em] font-medium mb-2">{g.tag}</p>
                  <p className="text-[#111111] group-hover:text-white text-sm font-medium leading-snug transition-colors" dangerouslySetInnerHTML={{ __html: g.label }} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section id="book-visit" data-testid="book-visit-section" className="py-32 md:py-40 bg-white">
        <div className="container-custom">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#111111] mb-3">
                Interested in {project.name}?
              </h2>
              <p className="text-[#999999] font-light">
                Schedule a visit or send an enquiry
              </p>
            </div>

            <div className="bg-[#F7F7F5] p-8 md:p-10">
              <LeadForm variant="light" source={`project-cta-${slug}`} projectName={project.name} />
            </div>

            <p className="text-center text-[#999999] text-sm mt-10 font-light tracking-wide">
              <a href="tel:+917498963176" className="text-[#666666] hover:text-[#111111] transition-colors">+91 7498963176</a>
              <span className="mx-4">|</span>
              <a href="mailto:Info@novahomes.in" className="text-[#666666] hover:text-[#111111] transition-colors">Info@novahomes.in</a>
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 bg-[#111111]">
        <div className="container-custom text-center">
          <Logo variant="light" className="justify-center mb-4" />
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-4">
            <Link to="/" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Home</Link>
            <span className="text-white/10">|</span>
            {Object.entries(projectData).filter(([s]) => s !== slug).map(([s, p]) => (
              <Link key={s} to={`/project/${s}`} className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">{p.name}</Link>
            ))}
            <span className="text-white/10">|</span>
            <Link to="/blog" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Insights</Link>
            <Link to="/rahatani-guide-2026" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Rahatani Guide</Link>
            <Link to="/flats-near-dmart-thergaon" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">Flats Near DMart</Link>
            <Link to="/3-bhk-collection-rahatani" className="text-white/60 hover:text-white text-sm font-light tracking-wide transition-colors">3 BHK Collection</Link>
          </div>
          <p className="text-white/20 text-sm font-light tracking-wide">© 2025 Nova Homes | Pune, Maharashtra</p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-black/[0.04] p-3 flex gap-2 lg:hidden z-40">
        <a
          href="tel:+917498963176"
          data-testid="mobile-call-btn"
          className="flex-1 bg-[#111111] text-white py-3 font-medium text-center text-sm flex items-center justify-center gap-2 tracking-wide"
        >
          <Phone size={15} />
          Call
        </a>
        <a
          href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-whatsapp-btn"
          className="flex-1 bg-[#111111] text-white py-3 font-medium text-center text-sm flex items-center justify-center gap-2 tracking-wide"
        >
          <MessageCircle size={15} />
          WhatsApp
        </a>
      </div>

      {/* ========== LIGHTBOX MODAL ========== */}
      {lightboxOpen && project.gallery && (
        <div 
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-[#111111]/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            data-testid="lightbox-close"
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors z-10"
          >
            <X size={22} />
          </button>

          {/* Download */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(project.gallery[lightboxIndex].path, `${project.name}_${project.gallery[lightboxIndex].label}`); }}
            data-testid="lightbox-download"
            className="absolute top-6 right-20 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors z-10"
            title="Download image"
          >
            <Download size={20} />
          </button>

          {/* Counter */}
          <div className="absolute top-7 left-6 text-white/30 text-sm font-light z-10">
            {lightboxIndex + 1} / {project.gallery.length}
          </div>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
            data-testid="lightbox-prev"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/30 hover:text-white transition-colors z-10"
          >
            <ChevronLeft size={28} />
          </button>

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
            data-testid="lightbox-next"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/30 hover:text-white transition-colors z-10"
          >
            <ChevronRight size={28} />
          </button>

          {/* Image */}
          <div className="max-w-[90vw] max-h-[80vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${BACKEND_URL}/api/floorplans/${project.gallery[lightboxIndex].path}`}
              alt={`${project.gallery[lightboxIndex].label} — ${project.seoLocation || project.name}`}
              data-testid="lightbox-image"
              className="max-w-full max-h-[80vh] object-contain"
            />
          </div>

          {/* Label */}
          <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-sm font-light tracking-wide">
            {project.gallery[lightboxIndex].label}
          </p>
        </div>
      )}
      {/* ========== AKSHAYA TRITIYA FESTIVAL POPUP ========== */}
      {showFestiveOffer && popupOpen && (
        <div 
          data-testid="festive-popup-overlay"
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setPopupOpen(false)}
        >
          <div 
            data-testid="festive-popup"
            className="relative max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(170deg, #0f0a04 0%, #1c1208 30%, #150e05 60%, #0f0a04 100%)',
              boxShadow: '0 0 80px rgba(212,175,55,0.08), 0 0 2px rgba(212,175,55,0.3), inset 0 1px 0 rgba(212,175,55,0.15)'
            }}
          >
            {/* Close */}
            <button
              onClick={() => setPopupOpen(false)}
              data-testid="festive-popup-close"
              className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center transition-colors z-10"
              style={{ color: 'rgba(212,175,55,0.3)' }}
              onMouseOver={(e) => e.currentTarget.style.color = 'rgba(212,175,55,0.8)'}
              onMouseOut={(e) => e.currentTarget.style.color = 'rgba(212,175,55,0.3)'}
            >
              <X size={16} strokeWidth={1.5} />
            </button>

            {/* Top gold line */}
            <div className="h-[1px] w-full" style={{ background: 'linear-gradient(90deg, transparent 5%, #D4AF37 30%, #F5E6C4 50%, #D4AF37 70%, transparent 95%)' }} />

            {/* Content */}
            <div className="px-10 md:px-14 py-14 md:py-16 text-center">

              {/* Minimal luxury emblem */}
              <div className="flex justify-center mb-8">
                <svg width="44" height="44" viewBox="0 0 44 44">
                  <path d="M22 2 L26 18 L42 22 L26 26 L22 42 L18 26 L2 22 L18 18 Z" fill="none" stroke="url(#emblemGrad)" strokeWidth="0.7" />
                  <circle cx="22" cy="22" r="3" fill="url(#emblemGrad)" opacity="0.5" />
                  <circle cx="22" cy="22" r="8" fill="none" stroke="url(#emblemGrad)" strokeWidth="0.4" opacity="0.4" />
                  <defs>
                    <linearGradient id="emblemGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#D4AF37" />
                      <stop offset="50%" stopColor="#F5E6C4" />
                      <stop offset="100%" stopColor="#D4AF37" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Festival label */}
              <p 
                className="text-xs uppercase font-medium mb-8"
                style={{ 
                  letterSpacing: '0.5em',
                  background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Akshaya Tritiya
              </p>

              {/* Headline */}
              <h3 
                className="font-heading text-3xl md:text-[2.5rem] font-light text-white/90 mb-3"
                style={{ lineHeight: 1.2, letterSpacing: '0.02em', textShadow: '0 0 40px rgba(212,175,55,0.1)' }}
              >
                This Auspicious Day
              </h3>
              <h3 
                className="font-heading text-3xl md:text-[2.5rem] font-light mb-10"
                style={{ 
                  lineHeight: 1.2, 
                  letterSpacing: '0.02em',
                  background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6C4 40%, #D4AF37 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 60px rgba(212,175,55,0.15)'
                }}
              >
                Invest in Your Legacy
              </h3>

              {/* Thin divider */}
              <div className="w-12 h-[1px] mx-auto mb-10" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }} />

              {/* Offer badges */}
              <div className="mb-4">
                <p 
                  className="text-[10px] uppercase font-medium mb-5"
                  style={{ letterSpacing: '0.4em', color: 'rgba(212,175,55,0.4)' }}
                >
                  Exclusive Offer
                </p>
                <div className="flex flex-wrap justify-center gap-4 mb-4">
                  {['No Stamp Duty', 'No Registration', 'No GST'].map((offer) => (
                    <span 
                      key={offer}
                      className="px-5 py-2.5 text-xs font-medium tracking-[0.15em] uppercase"
                      style={{ 
                        border: '1px solid rgba(212,175,55,0.25)',
                        color: '#F5E6C4',
                        textShadow: '0 0 12px rgba(212,175,55,0.2)'
                      }}
                    >
                      {offer}
                    </span>
                  ))}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '10px', letterSpacing: '0.1em' }}>*Conditions apply</p>
              </div>

              {/* Spacer */}
              <div className="h-8" />

              {/* Project context */}
              <p className="text-white/30 text-sm font-light mb-8" style={{ letterSpacing: '0.05em' }}>
                <span className="text-white/60">{project.name}</span>
                <span className="mx-2 text-white/10">|</span>
                {project.type}
              </p>

              {/* CTA */}
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hi, I'm interested in the Akshaya Tritiya offer on ${project.name}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="festive-popup-cta"
                className="inline-flex items-center gap-3 px-10 py-4 font-medium text-sm tracking-[0.15em] uppercase transition-all duration-300 hover:scale-[1.02]"
                style={{ 
                  background: 'linear-gradient(135deg, #D4AF37, #F5E6C4, #D4AF37)',
                  color: '#0f0a04',
                  boxShadow: '0 0 30px rgba(212,175,55,0.2), 0 4px 20px rgba(0,0,0,0.3)'
                }}
              >
                <MessageCircle size={15} />
                Unlock This Offer
              </a>

              <p className="mt-10" style={{ color: 'rgba(255,255,255,0.12)', fontSize: '10px', letterSpacing: '0.2em' }}>
                VALID TILL 19TH APRIL 2026
              </p>
            </div>

            {/* Bottom gold line */}
            <div className="h-[1px] w-full" style={{ background: 'linear-gradient(90deg, transparent 5%, #D4AF37 30%, #F5E6C4 50%, #D4AF37 70%, transparent 95%)' }} />
          </div>
        </div>
      )}

      <LeadMagnetModal
        open={floorPlansLeadOpen}
        onClose={() => setFloorPlansLeadOpen(false)}
        source={`project-${slug}-floor-plans`}
        variant="floor-plans"
        projectSlug={slug}
      />

      <LeadMagnetModal
        open={brochureLeadOpen}
        onClose={() => setBrochureLeadOpen(false)}
        source={`project-${slug}-brochure`}
        variant="brochure"
        projectSlug={slug}
      />

      {/* New unified Floor Plans + Price Sheet popup */}
      <FloorPlanPopup
        open={floorPlanPopupOpen}
        onClose={() => setFloorPlanPopupOpen(false)}
        project={project.name}
        sourceTag={floorPlanPopupSource}
      />

    </div>
  );
};

export default ProjectPage;
